import { useCallback, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import { PageMeta } from "@/components/common/PageMeta";
import { FUND_COMPLIANCE_AUTH_INVALIDATED_EVENT } from "@/lib/fund-compliance/auth-events";
import {
  exchangeFundComplianceAccessCode,
  fetchFundComplianceCurrentUser,
  FundComplianceAuthError,
} from "@/lib/fund-compliance/api";

interface GateState {
  checking: boolean;
  authed: boolean;
}

export function FundComplianceAccessGate() {
  const [{ checking, authed }, setGateState] = useState<GateState>({ checking: true, authed: false });
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const verifySession = useCallback(async () => {
    try {
      await fetchFundComplianceCurrentUser();
      setGateState({ checking: false, authed: true });
    } catch (err) {
      if (err instanceof FundComplianceAuthError) {
        setGateState({ checking: false, authed: false });
        return;
      }
      setError("Unable to verify session. Please try again.");
      setGateState({ checking: false, authed: false });
    }
  }, []);

  useEffect(() => {
    void verifySession();
  }, [verifySession]);

  useEffect(() => {
    function handleAuthInvalidated() {
      setSubmitting(false);
      setError(null);
      setValue("");
      setGateState({ checking: false, authed: false });
    }

    window.addEventListener(FUND_COMPLIANCE_AUTH_INVALIDATED_EVENT, handleAuthInvalidated);
    return () => window.removeEventListener(FUND_COMPLIANCE_AUTH_INVALIDATED_EVENT, handleAuthInvalidated);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!value.trim()) return;

      setSubmitting(true);
      setError(null);

      try {
        await exchangeFundComplianceAccessCode(value);
        await verifySession();
        setValue("");
      } catch (err) {
        if (err instanceof FundComplianceAuthError) {
          setError("Invalid or expired access code.");
        } else if (err instanceof Error) {
          setError(err.message || "Unable to complete sign-in.");
        } else {
          setError("Unable to complete sign-in.");
        }
        setGateState({ checking: false, authed: false });
      } finally {
        setSubmitting(false);
      }
    },
    [value, verifySession]
  );

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <PageMeta
          title="Agent Access | COSX"
          description="Enter the access code to use the Fund Compliance Agent."
          path="/demo/fund-compliance-agent"
          noindex
        />
        <div className="text-xs uppercase tracking-[0.24em] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">
          Checking session
        </div>
      </div>
    );
  }

  if (authed) return <Outlet />;

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <PageMeta
        title="Agent Access | COSX"
        description="Enter the access code to use the Fund Compliance Agent."
        path="/demo/fund-compliance-agent"
        noindex
      />
      <form onSubmit={handleSubmit} className="flex flex-col items-center gap-4 p-8 border border-[var(--color-border)] max-w-sm w-full">
        <div className="text-sm font-[family-name:var(--font-mono)] font-medium tracking-widest uppercase text-[var(--color-fg)]">
          Agent Access
        </div>
        <p className="text-xs text-[var(--color-fg-secondary)] text-center">
          Enter the access code to use the Fund Compliance Agent.
        </p>
        <input
          type="password"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          placeholder="access code"
          autoFocus
          className="w-full px-4 py-2.5 border border-[var(--color-border)] bg-transparent text-[var(--color-fg)] font-[family-name:var(--font-mono)] text-sm text-center tracking-widest outline-none focus:border-[var(--color-fg-secondary)] transition-colors"
        />
        {error && (
          <p className="text-[10px] text-red-400 font-[family-name:var(--font-mono)] text-center">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={submitting || !value.trim()}
          className="w-full py-2.5 text-xs font-medium bg-[var(--color-fg)] text-[var(--color-bg)] rounded-full hover:opacity-90 disabled:opacity-50 transition-opacity cursor-pointer"
        >
          {submitting ? "Checking..." : "Continue"}
        </button>
      </form>
    </div>
  );
}
